import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Download,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  BarChart,
  DollarSign,
  Package,
  History,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Layout from '../components/Layout';
import ClientForm from '../components/ClientForm';
import clientService from '../services/clientService';
import authService from '../services/authService';

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  // Data states
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // UI states
  const [activeTab, setActiveTab] = useState('basic');
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Inventory pagination
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(10);
  const [inventoryTotalCount, setInventoryTotalCount] = useState(0);

  // Auto-hide notification
  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [notification.open]);

  // Permission check
  const currentUser = authService.getCurrentUser();
  const canEditClient = ['admin', 'warehouse_manager'].includes(currentUser?.role);

  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);
      const clientResponse = await clientService.getClient(clientId);

      if (clientResponse.client) {
        setClient(clientResponse.client);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      setNotification({
        open: true,
        message: '加载客户信息失败',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const fetchClientInventory = useCallback(async () => {
    try {
      setInventoryLoading(true);
      const response = await clientService.getClientInventory(
        clientId,
        inventoryPage + 1,
        inventoryRowsPerPage
      );

      if (response.products) {
        setInventory(response.products);
        setInventoryTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error('Error fetching client inventory:', error);
      setNotification({
        open: true,
        message: '加载库存信息失败',
        severity: 'error'
      });
    } finally {
      setInventoryLoading(false);
    }
  }, [clientId, inventoryPage, inventoryRowsPerPage]);

  useEffect(() => {
    fetchClientData();
  }, [clientId, fetchClientData]);

  useEffect(() => {
    if (activeTab === 'inventory') {
      fetchClientInventory();
    }
  }, [activeTab, inventoryPage, inventoryRowsPerPage, fetchClientInventory]);

  const handleEditClient = () => {
    setEditFormOpen(true);
  };

  const handleDeleteClient = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    try {
      await clientService.deleteClient(clientId);
      setNotification({
        open: true,
        message: '客户删除成功',
        severity: 'success'
      });
      navigate('/settings/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      setNotification({
        open: true,
        message: '删除客户失败',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      await clientService.updateClient(clientId, formData);
      setNotification({
        open: true,
        message: '客户信息更新成功',
        severity: 'success'
      });
      setEditFormOpen(false);
      fetchClientData();
    } catch (error) {
      console.error('Error updating client:', error);
      throw new Error(error.response?.data?.message || '更新失败');
    } finally {
      setFormLoading(false);
    }
  };

  const handleExportClientData = () => {
    setNotification({
      open: true,
      message: '导出功能开发中',
      severity: 'info'
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'pending': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '活跃';
      case 'inactive': return '非活跃';
      case 'pending': return '待审核';
      default: return status;
    }
  };

  const inventoryTotalPages = Math.ceil(inventoryTotalCount / inventoryRowsPerPage);

  const renderBasicInfo = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <Card className="border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              基本信息
            </h3>
            <Separator className="mb-4" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">客户编号</p>
                  <p className="font-medium">{client.client_id}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">客户名称</p>
                  <p className="font-medium">{client.client_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">主要联系人</p>
                  <p>{client.contact_info?.primary_contact || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">客户状态</p>
                  <Badge variant={getStatusVariant(client.status)}>
                    {getStatusLabel(client.status)}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">邮箱地址</p>
                  <p>{client.contact_info?.email || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">联系电话</p>
                  <p>{client.contact_info?.phone || '-'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:col-span-2">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">地址</p>
                  <p>{client.contact_info?.address || '-'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              企业信息
            </h3>
            <Separator className="mb-4" />

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">行业分类</p>
                  <p>{client.business_info?.industry || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">企业规模</p>
                  <p>
                    {client.business_info?.company_size === 'small' ? '小型企业' :
                     client.business_info?.company_size === 'medium' ? '中型企业' :
                     client.business_info?.company_size === 'large' ? '大型企业' : '-'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <BarChart className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">信用等级</p>
                  <p>{client.business_info?.credit_rating || '-'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              商务偏好
            </h3>
            <Separator className="mb-4" />

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">首选货币</p>
                  <p>{client.preferences?.preferred_currency || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">付款条件</p>
                <p>{client.preferences?.payment_terms || '-'}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">折扣等级</p>
                <p>
                  {client.preferences?.discount_tier === 'gold' ? '金牌客户' :
                   client.preferences?.discount_tier === 'silver' ? '银牌客户' :
                   client.preferences?.discount_tier === 'bronze' ? '铜牌客户' :
                   client.preferences?.discount_tier === 'standard' ? '标准客户' : '-'}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">税率</p>
                <p>{client.preferences?.tax_rate ? `${(client.preferences.tax_rate * 100).toFixed(1)}%` : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderInventory = () => (
    <Card className="border bg-card">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          客户库存概览
        </h3>
        <Separator className="mb-4" />

        {inventoryLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : inventory.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">暂无库存数据</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium text-muted-foreground">产品名称</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">规格</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">分类</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">库存数量</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">单价</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">总值</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.map((item) => (
                    <tr key={item._id} className="border-b hover:bg-muted/50">
                      <td className="p-3">{item.name}</td>
                      <td className="p-3">{item.specification || '-'}</td>
                      <td className="p-3">{item.category || '-'}</td>
                      <td className="p-3 text-right">{item.quantity || 0}</td>
                      <td className="p-3 text-right">
                        ¥{item.quotation_price ? item.quotation_price.toFixed(2) : '0.00'}
                      </td>
                      <td className="p-3 text-right">
                        ¥{((item.quantity || 0) * (item.quotation_price || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">每页显示</span>
                <Select
                  value={String(inventoryRowsPerPage)}
                  onValueChange={(value) => {
                    setInventoryRowsPerPage(parseInt(value, 10));
                    setInventoryPage(0);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  第 {inventoryPage * inventoryRowsPerPage + 1}-{Math.min((inventoryPage + 1) * inventoryRowsPerPage, inventoryTotalCount)} 条，共 {inventoryTotalCount} 条
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setInventoryPage(p => p - 1)}
                  disabled={inventoryPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setInventoryPage(p => p + 1)}
                  disabled={inventoryPage >= inventoryTotalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout title="客户详情" activeTab="settings">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout title="客户详情" activeTab="settings">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <Alert variant="destructive">
            <AlertDescription>
              客户信息不存在或加载失败
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="客户详情" activeTab="settings">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Notification Toast */}
        {notification.open && (
          <div className="fixed top-4 right-4 z-50">
            <Alert
              variant={notification.severity === 'error' ? 'destructive' : 'default'}
              className={cn(
                'shadow-lg',
                notification.severity === 'success' && 'border-success/50 bg-success/10',
                notification.severity === 'info' && 'border-info/50 bg-info/10'
              )}
            >
              <AlertDescription className={cn(
                notification.severity === 'success' && 'text-success',
                notification.severity === 'info' && 'text-info'
              )}>
                {notification.message}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings/clients')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">
              {client.client_name}
            </h1>
            <p className="text-muted-foreground">
              客户编号: {client.client_id}
            </p>
          </div>

          {canEditClient && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportClientData}>
                <Download className="h-4 w-4 mr-2" />
                导出数据
              </Button>
              <Button variant="outline" onClick={handleEditClient}>
                <Pencil className="h-4 w-4 mr-2" />
                编辑信息
              </Button>
              <Button variant="destructive" onClick={handleDeleteClient}>
                <Trash2 className="h-4 w-4 mr-2" />
                删除客户
              </Button>
            </div>
          )}
        </div>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="basic">基本信息</TabsTrigger>
            <TabsTrigger value="inventory">库存概览</TabsTrigger>
            <TabsTrigger value="history">活动历史</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            {renderBasicInfo()}
          </TabsContent>

          <TabsContent value="inventory">
            {renderInventory()}
          </TabsContent>

          <TabsContent value="history">
            <Card className="border bg-card">
              <CardContent className="py-12 text-center">
                <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  活动历史功能开发中
                </h3>
                <p className="text-sm text-muted-foreground">
                  将显示客户的操作记录和活动历史
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Form Dialog */}
        <ClientForm
          open={editFormOpen}
          onClose={() => setEditFormOpen(false)}
          onSubmit={handleFormSubmit}
          client={client}
          loading={formLoading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                确认删除客户
              </DialogTitle>
              <DialogDescription>
                确定要删除客户 "{client.client_name}" 吗？
                <br />
                <strong>此操作无法撤销</strong>，相关的产品配置和库存数据也会被删除。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                取消
              </Button>
              <Button variant="destructive" onClick={confirmDeleteClient}>
                确认删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientDetailPage;
